import * as React from 'react';
import classnames from '../utils/classnames.tsx';
import { typeOfComponent } from '../utils/children.ts';
import { withIcon } from './Icon.tsx';
import {
    faCheckCircle,
    faExclamationCircle,
    faInfoCircle,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';

const InfoIcon = withIcon(faInfoCircle);
const WarningIcon = withIcon(faTriangleExclamation);
const ErrorIcon = withIcon(faExclamationCircle);
const SuccessIcon = withIcon(faCheckCircle);

const SEVERITY_LEVELS = ['info', 'warning', 'error', 'success'];

interface IProps {
    severity?: string;
    children?: any;
    className?: string;
}

export const AlertIcon = ({ severity }) => {
    switch (severity) {
        case 'info':
            return <InfoIcon />;
        case 'warning':
            return <WarningIcon />;
        case 'error':
            return <ErrorIcon />;
        case 'success':
            return <SuccessIcon />;
        default:
            return null;
    }
};

export const Alert = ({ severity, className, children }: IProps) => {
    const severityLevel = SEVERITY_LEVELS.includes(severity) ? severity : SEVERITY_LEVELS[0];
    const showIcon = SEVERITY_LEVELS.includes(severity);

    const title = React.Children.toArray(children).find((child) => typeOfComponent(child) === 'AlertTitle');

    const content = React.Children.map(children, (child) => {
        if (typeOfComponent(child) === 'AlertTitle') return null;
        if (child && typeOfComponent(child) === 'AlertContent') return child;
        return <AlertContent>{child}</AlertContent>;
    });

    return (
        <div
            className={classnames(
                'bg-surface-strong text-content-strong text-sm px-4 py-3 rounded-lg flex gap-2',
                severityLevel === 'info' && '',
                severityLevel === 'warning' && 'bg-surface-warning-weak text-content-warning-strong',
                severityLevel === 'error' && 'bg-surface-danger-weak text-content-danger-strong',
                severityLevel === 'success' && 'bg-surface-success-weak text-content-success-strong',
                className
            )}
        >
            {Boolean(showIcon || title) && (
                <AlertTitle>
                    {showIcon && (
                        <span className="flex-none">
                            <AlertIcon severity={severityLevel} />
                        </span>
                    )}
                    {title}
                </AlertTitle>
            )}

            <>{content}</>
        </div>
    );
};
Alert.displayName = 'Alert';

export const AlertTitle = (props) => <h4 className="font-medium text-sm inline-flex gap-2">{props.children}</h4>;
AlertTitle.displayName = 'AlertTitle';

export const AlertContent = (props) => <div className="w-full text-sm">{props.children}</div>;
AlertContent.displayName = 'AlertContent';

export default Alert;
